// store/useNotificationStore.ts — In-app notification state

import { create } from 'zustand';
import type { Notification } from '@/types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;

  // Actions
  addNotification: (notification: Notification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  unreadCount: 0,

  addNotification: (notification) =>
    set((s) => ({
      notifications: [notification, ...s.notifications].slice(0, 100), // Cap at 100
      unreadCount: s.unreadCount + (notification.isRead ? 0 : 1),
    })),

  markRead: (id) =>
    set((s) => {
      const notification = s.notifications.find((n) => n.id === id);
      if (!notification || notification.isRead) return s;
      return {
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      };
    }),

  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    })),

  dismiss: (id) =>
    set((s) => {
      const notification = s.notifications.find((n) => n.id === id);
      return {
        notifications: s.notifications.filter((n) => n.id !== id),
        unreadCount: notification?.isRead === false
          ? Math.max(0, s.unreadCount - 1)
          : s.unreadCount,
      };
    }),

  clearAll: () =>
    set({ notifications: [], unreadCount: 0 }),
}));
